import { GeometryType } from '../geometry/GeometryType'

/**
 * 所有实体与曲面共享的逻辑身份元数据。
 *
 * 设计边界：
 * 1. `modelBodyId` 标识跨特征版本保持稳定的逻辑实体；
 * 2. `modelBodyRevisionId` 标识当前 Worker 几何版本，必须等于 `GeometryObject.id`；
 * 3. 来源类型、后端 bodyBindingId 和场景节点 ID 都不得参与特征生命周期判断。
 */
export interface ModelBodyIdentityMetadata {
  /** 跨修改型特征保持稳定的逻辑实体 ID。 */
  modelBodyId?: string
  /** 当前活动几何版本 ID。 */
  modelBodyRevisionId?: string
  /** 外部数据边界映射到该逻辑实体的精确别名。 */
  modelBodyAliasIds?: readonly string[]
  /** 已被当前结果体替换的上游几何版本 ID。 */
  consumedModelBodyRevisionIds?: readonly string[]
  /** 多实体合并时被吸收的其它逻辑实体 ID。 */
  consumedModelBodyIds?: readonly string[]
}

/**
 * 修改型或合并型特征写入结果体的统一替换元数据。
 */
export interface ModelBodyReplacementMetadata extends ModelBodyIdentityMetadata {
  /** 结果体继承的主逻辑实体 ID。 */
  modelBodyId?: string
  /** 已被当前结果体替换的上游几何版本 ID。 */
  consumedModelBodyRevisionIds?: readonly string[]
  /** 多实体合并时被吸收的其它逻辑实体 ID。 */
  consumedModelBodyIds?: readonly string[]
}

/**
 * 判断 Worker 对象是否属于统一实体生命周期。
 *
 * @param object Worker 几何对象。
 * @returns 实体或曲面返回 true。
 */
export function isModelBodyGeometryObject(
  object: GeometryObject | null | undefined,
): object is GeometryObject {
  return object?.type === GeometryType.SOLID || object?.type === GeometryType.SURFACE
}

/**
 * 规范化可选字符串。
 *
 * @param value 未知字段值。
 * @returns 非空字符串；其它情况返回 null。
 */
function normalizeOptionalString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null
  }
  const normalizedValue = value.trim()
  return normalizedValue.length > 0 ? normalizedValue : null
}

/**
 * 向数组追加去重后的字符串。
 *
 * @param target 目标数组。
 * @param value 候选字符串。
 */
function appendUniqueString(target: string[], value: unknown): void {
  const normalizedValue = normalizeOptionalString(value)
  if (!normalizedValue || target.includes(normalizedValue)) {
    return
  }
  target.push(normalizedValue)
}

/**
 * 从未知字段读取去重字符串数组。
 *
 * @param value 未知字段值。
 * @returns 规范化后的字符串数组。
 */
export function readModelBodyMetadataStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return []
  }
  const result: string[] = []
  value.forEach((item) => appendUniqueString(result, item))
  return result
}

/**
 * 判断当前实体是否属于指定的模型实体血缘。
 *
 * 设计边界：
 * 1. `modelBodyId` 命中当前逻辑实体时表示原位延续；
 * 2. 切除把一个实体分裂为多个实体时，新增片段通过 consumed/alias 元数据保留共同来源；
 * 3. 本函数只判断实体血缘，不判断 Feature owner，也不参与边级 topology 重定位。
 *
 * @param metadata 当前实体的 Worker 或 Scene 元数据。
 * @param fallbackObjectId 缺少统一实体字段时使用的当前对象 ID。
 * @param sourceModelBodyId 引用创建时记录的逻辑实体 ID。
 * @param sourceModelBodyRevisionId 引用创建时记录的实体修订 ID。
 * @returns 当前实体是否属于指定来源实体的延续或分裂结果。
 */
export function isModelBodyMetadataInLineage(
  metadata: Record<string, unknown> | null | undefined,
  fallbackObjectId: string,
  sourceModelBodyId: string | null | undefined,
  sourceModelBodyRevisionId: string | null | undefined,
): boolean {
  const normalizedSourceBodyId = normalizeOptionalString(sourceModelBodyId)
  const normalizedSourceRevisionId = normalizeOptionalString(sourceModelBodyRevisionId)
  if (!normalizedSourceBodyId && !normalizedSourceRevisionId) {
    return false
  }

  const currentMetadata = metadata ?? {}
  const currentBodyId = normalizeOptionalString(currentMetadata.modelBodyId)
    ?? normalizeOptionalString(fallbackObjectId)
  const currentRevisionId = normalizeOptionalString(currentMetadata.modelBodyRevisionId)
    ?? normalizeOptionalString(fallbackObjectId)
  if (
    (normalizedSourceBodyId && currentBodyId === normalizedSourceBodyId)
    || (normalizedSourceRevisionId && currentRevisionId === normalizedSourceRevisionId)
  ) {
    return true
  }

  const aliasIds = readModelBodyMetadataStringArray(currentMetadata.modelBodyAliasIds)
  const consumedBodyIds = readModelBodyMetadataStringArray(currentMetadata.consumedModelBodyIds)
  const consumedRevisionIds = readModelBodyMetadataStringArray(
    currentMetadata.consumedModelBodyRevisionIds,
  )
  return Boolean(
    normalizedSourceBodyId
    && (
      aliasIds.includes(normalizedSourceBodyId)
      || consumedBodyIds.includes(normalizedSourceBodyId)
      || consumedRevisionIds.includes(normalizedSourceBodyId)
    ),
  ) || Boolean(
    normalizedSourceRevisionId
    && consumedRevisionIds.includes(normalizedSourceRevisionId),
  )
}

/**
 * 读取实体逻辑身份；旧对象缺少新字段时统一回退到对象 ID。
 *
 * 该回退只用于持久化迁移，不根据 imported/bodyBinding/assemblyNode 等来源字段猜测身份。
 *
 * @param object Worker 几何对象。
 * @returns 统一实体身份；非实体对象返回 null。
 */
export function resolveModelBodyIdentity(
  object: GeometryObject | null | undefined,
): Required<Pick<ModelBodyIdentityMetadata, 'modelBodyId' | 'modelBodyRevisionId'>> | null {
  if (!isModelBodyGeometryObject(object)) {
    return null
  }
  const metadata = object.metadata ?? {}
  return {
    modelBodyId: normalizeOptionalString(metadata.modelBodyId) ?? object.id,
    modelBodyRevisionId: normalizeOptionalString(metadata.modelBodyRevisionId) ?? object.id,
  }
}

/**
 * 在 Worker 注册边界为实体写入统一身份并维护活动逻辑 ID 唯一性。
 *
 * @param object 待注册对象。
 * @param activeObjects 注册前仍处于活动状态的其它对象。
 * @returns 原对象；函数会原位规范化其 metadata。
 */
export function normalizeGeometryObjectModelBodyIdentity(
  object: GeometryObject,
  activeObjects: readonly GeometryObject[] = [],
): GeometryObject {
  if (!isModelBodyGeometryObject(object)) {
    return object
  }

  const metadata = object.metadata ?? {}
  const requestedModelBodyId = normalizeOptionalString(metadata.modelBodyId) ?? object.id
  const consumedRevisionIds = readModelBodyMetadataStringArray(
    metadata.consumedModelBodyRevisionIds,
  )
  const conflictingObject = activeObjects.find((candidate) => {
    if (!isModelBodyGeometryObject(candidate) || candidate.id === object.id) {
      return false
    }
    const candidateIdentity = resolveModelBodyIdentity(candidate)
    if (!candidateIdentity || candidateIdentity.modelBodyId !== requestedModelBodyId) {
      return false
    }
    /**
     * 允许结果先注册、上游随后移除的原子替换过程；拆分出的第二个活动结果不会消费
     * 第一个结果版本，因此会在这里获得自己的逻辑实体 ID。
     */
    return !consumedRevisionIds.includes(candidateIdentity.modelBodyRevisionId)
  })
  const modelBodyId = conflictingObject ? object.id : requestedModelBodyId

  object.metadata = {
    ...metadata,
    modelBodyId,
    modelBodyRevisionId: object.id,
  }
  return object
}

/**
 * 为修改型特征构建来源无关的实体替换元数据。
 *
 * @param sourceObject 被当前特征替换的上游实体。
 * @returns 可写入结果体 metadata 的统一字段。
 */
export function buildModelBodyReplacementMetadata(
  sourceObject: GeometryObject | null | undefined,
): ModelBodyReplacementMetadata {
  const identity = resolveModelBodyIdentity(sourceObject)
  if (!identity || !sourceObject) {
    return {}
  }

  const consumedModelBodyRevisionIds = readModelBodyMetadataStringArray(
    sourceObject.metadata?.consumedModelBodyRevisionIds,
  )
  appendUniqueString(consumedModelBodyRevisionIds, identity.modelBodyRevisionId)
  const consumedModelBodyIds = readModelBodyMetadataStringArray(
    sourceObject.metadata?.consumedModelBodyIds,
  )
  const modelBodyAliasIds = readModelBodyMetadataStringArray(
    sourceObject.metadata?.modelBodyAliasIds,
  )

  return {
    modelBodyId: identity.modelBodyId,
    consumedModelBodyRevisionIds,
    ...(modelBodyAliasIds.length > 0 ? { modelBodyAliasIds } : {}),
    ...(consumedModelBodyIds.length > 0 ? { consumedModelBodyIds } : {}),
  }
}

/**
 * 合并多个实体替换元数据，并以首个有效逻辑实体作为结果主实体。
 *
 * @param metadataList 按主实体优先级排列的替换元数据。
 * @returns 合并后的统一替换字段。
 */
export function mergeModelBodyReplacementMetadata(
  metadataList: readonly ModelBodyReplacementMetadata[],
): ModelBodyReplacementMetadata {
  const primaryModelBodyId = metadataList
    .map((metadata) => normalizeOptionalString(metadata.modelBodyId))
    .find((modelBodyId): modelBodyId is string => Boolean(modelBodyId))
  const consumedModelBodyRevisionIds: string[] = []
  const consumedModelBodyIds: string[] = []
  const modelBodyAliasIds: string[] = []

  metadataList.forEach((metadata) => {
    readModelBodyMetadataStringArray(metadata.consumedModelBodyRevisionIds)
      .forEach((revisionId) => appendUniqueString(consumedModelBodyRevisionIds, revisionId))
    readModelBodyMetadataStringArray(metadata.consumedModelBodyIds)
      .forEach((modelBodyId) => appendUniqueString(consumedModelBodyIds, modelBodyId))
    readModelBodyMetadataStringArray(metadata.modelBodyAliasIds)
      .forEach((aliasId) => appendUniqueString(modelBodyAliasIds, aliasId))

    const modelBodyId = normalizeOptionalString(metadata.modelBodyId)
    if (modelBodyId && primaryModelBodyId && modelBodyId !== primaryModelBodyId) {
      appendUniqueString(consumedModelBodyIds, modelBodyId)
    }
  })

  if (!primaryModelBodyId && consumedModelBodyRevisionIds.length === 0) {
    return {}
  }
  return {
    ...(primaryModelBodyId ? { modelBodyId: primaryModelBodyId } : {}),
    ...(modelBodyAliasIds.length > 0 ? { modelBodyAliasIds } : {}),
    ...(consumedModelBodyRevisionIds.length > 0 ? { consumedModelBodyRevisionIds } : {}),
    ...(consumedModelBodyIds.length > 0 ? { consumedModelBodyIds } : {}),
  }
}
